import { withEventContext } from "@codat/analytics";
import { Modal, Typography } from "@codat/orchard-ui";
import {
  useManualIntegrationQuery,
  useManualIntegrationsQuery,
} from "api/company";
import { canSkip } from "contexts/config-fn";
import { ConfigStatusType, useConfig } from "contexts/ConfigContext";
import { SourceTypeStatus, useSourceTypes } from "contexts/SourceTypesContext";
import { useCreateConnectionClickHandler } from "hooks/useCreateConnectionClickHandler";
import { useGATrackingEvent } from "hooks/useGATracking";
import useNextSourceTypeRedirectHandler from "hooks/useNextSourceTypeRedirectHandler";
import { useSourceTypeDisplayProps } from "hooks/useSourceTypeDisplayProps";
import { useSourceTypeResolver } from "hooks/useSourceTypeResolver";
import { useEffect, useState } from "react";
import { RouteComponentProps, useHistory, useLocation } from "react-router-dom";
import { formatUrl, usePageViewedEvent } from "utils";

import Button from "components/Button";
import ExitModal from "components/ExitModal";
import LinkPageRoot from "components/LinkPageRoot";
import WarningDialog from "components/WarningDialog";
import { CompanySourceTypeParams, routes } from "config/urls";

import { TermsAndConditionsLink } from "../CompanyConsent/CompanyConsent";
import { FileUploadLink } from "../FileUpload";
import styles from "./CustomIntro.module.css";

function CustomIntroContent({
  match,
}: RouteComponentProps<CompanySourceTypeParams>) {
  const history = useHistory();
  const { sourceTypes, status: sourceTypesStatus } = useSourceTypes();
  const { state } = useConfig();
  const locationSearch = useLocation().search;
  const { logGAEvent } = useGATrackingEvent();

  const [isWarningModal, setWarningModal] = useState(false);
  const [isExitModal, setExitModal] = useState(false);

  const { currentSourceType, nextPrioritySourceType } = useSourceTypeResolver(
    match.params,
    sourceTypes
  );

  const displayProps = useSourceTypeDisplayProps(currentSourceType);

  const [{ data: integrationsData }, executeGetIntegrations] =
    useManualIntegrationsQuery(currentSourceType?.integrationsUri ?? "");

  if (integrationsData?.totalResults === 0) {
    window.location.assign(routes.notfound);
  }

  const integration = integrationsData?.results
    ? integrationsData?.results[0]
    : undefined;

  usePageViewedEvent({
    ready: !!integration,
  });

  const [{ data: integrationBrandingData }, executeGetIntegrationBranding] =
    useManualIntegrationQuery();

  useEffect(() => {
    if (currentSourceType && executeGetIntegrations) {
      executeGetIntegrations();
    }
  }, [executeGetIntegrations, currentSourceType]);

  useEffect(() => {
    if (integration && executeGetIntegrationBranding) {
      executeGetIntegrationBranding({
        companyId: match.params.companyId,
        platformKey: integration.key,
      });
    }
  }, [executeGetIntegrationBranding, integration, match.params.companyId]);

  const isComplete =
    !!integrationBrandingData &&
    sourceTypesStatus === SourceTypeStatus.READY &&
    state.status === ConfigStatusType.READY;

  const handleExitButtonClick = state.data.hasCustomRedirect
    ? () => {
        logGAEvent("Link Flow", "ExitIcon", match.params.companyId);
        setExitModal(true);
      }
    : undefined;

  const [isLoading, createConnection] = useCreateConnectionClickHandler({
    companyId: match.params.companyId,
    platformKey: integration?.key,
    sourceType: currentSourceType?.sourceType,
  });

  const onContinueButtonClick = () => {
    if (currentSourceType?.displayWarning) {
      setWarningModal(true);
      return;
    }

    createConnection();
  };

  const handleWarningCancel = () => {
    history.push({
      pathname: formatUrl<CompanySourceTypeParams>(routes.company.revisit, {
        companyId: match.params.companyId,
        sourceType: currentSourceType?.sourceType,
      }),
      search: locationSearch,
    });
  };

  const handleSkip = useNextSourceTypeRedirectHandler(
    match.params.companyId,
    nextPrioritySourceType,
    currentSourceType && !nextPrioritySourceType
  );

  const secondaryCtaProps = (() => {
    if (canSkip(currentSourceType, state.data)) {
      return {
        label: "Skip",
        "data-testid": "skip-btn",
        "aria-label": "Skip",
        onClick: handleSkip,
        analyticsName: "Skip",
      };
    } else {
      return undefined;
    }
  })();

  return (
    <>
      <LinkPageRoot
        isComplete={isComplete}
        showLinkedAccounts
        onExitClick={handleExitButtonClick}
        currentSourceType={currentSourceType}
        integrationLogo={integrationBrandingData?.logo.square.image}
        isPlaidClientLimitReached={
          integration?.key === "suuo" && integration?.clientLimitReached
        }
        secondaryCtaProps={secondaryCtaProps}
      >
        <div className={styles.container}>
          {integrationBrandingData?.logo?.square?.image?.src && (
            <img
              className={styles.image}
              src={integrationBrandingData.logo.square.image.src}
              alt={integrationBrandingData.logo.square.image.alt}
            />
          )}
          <Typography
            className={styles.header}
            data-testid="page-title"
            variant="h3"
            as="h2"
          >
            Connect your bank
          </Typography>
          <Typography
            className={styles.body}
            data-testid="page-body"
            variant="p"
            as="p"
          >
            We use {integration?.name} to quickly and securely connect to your
            bank.
          </Typography>
          <Typography
            className={styles.body}
            data-testid="consent-permission-notice"
            variant="p"
            as="p"
          >
            {state.data.pageConfiguration.dataAccess.consentsBody.banking}
          </Typography>
          <div className={styles.body}>
            <TermsAndConditionsLink
              link={
                state.data?.pageConfiguration?.dataAccess?.termsAndConditions
              }
            />
          </div>
          <div className={styles.actions}>
            <Button
              className={styles.continueBtn}
              analyticsName="Continue"
              label="Continue"
              type="button"
              data-testid="primary-next-btn"
              onClick={onContinueButtonClick}
              isLoading={isLoading}
              spinnerAlign="left"
            />
            {displayProps.link.canUpload && currentSourceType && (
              <FileUploadLink sourceType={currentSourceType.sourceType} />
            )}
          </div>
        </div>
      </LinkPageRoot>
      <Modal onClose={() => setWarningModal(false)}>
        {isWarningModal && (
          <WarningDialog
            title={displayProps.link.warningTitle}
            subText={displayProps.link.warningMessage}
            primaryCtaProps={{
              label: "Back",
              onClick: handleWarningCancel,
              analyticsName: "Back",
            }}
            secondaryCtaProps={{
              label: "Continue",
              onClick: createConnection,
              analyticsName: "Continue",
            }}
          />
        )}
      </Modal>
      <ExitModal
        isOpen={isExitModal}
        onClose={() => setExitModal(false)}
        companyId={match.params.companyId}
        sourceType={currentSourceType?.sourceType}
      />
    </>
  );
}

export const CustomIntro = withEventContext<
  RouteComponentProps<CompanySourceTypeParams> & JSX.IntrinsicAttributes
>(CustomIntroContent, "Plaid page");
